/**
 * iter-121.47 — Gallery filter toolbar.
 *
 * Pure render component. Receives the current filter values + onChange
 * handlers from the parent. No data fetching. Keeps the screenshot's
 * layout: search left, Type / Workspace / Last-modified dropdowns,
 * Clear, then Grid/List view toggle on the right.
 */
import React from "react";
import { Search, Calendar, LayoutGrid, List as ListIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export const KIND_OPTIONS = [
    { value: "all", label: "All types" },
    { value: "kpi", label: "KPI" },
    { value: "chart", label: "Chart" },
    { value: "table", label: "Table" },
    { value: "insight", label: "Insight" },
];

export const MODIFIED_OPTIONS = [
    { value: "any", label: "Any time" },
    { value: "24h", label: "Last 24 hours" },
    { value: "7d",  label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
];

export default function GalleryFilters({
    q, setQ,
    kind, setKind,
    workspace, setWorkspace, workspaces,
    modified, setModified,
    viewMode, setViewMode,
    onClear,
}) {
    const hasFilter = (q && q.length > 0) || kind !== "all" || workspace !== "all" || modified !== "any";
      const isGridView = viewMode === "tiles" || viewMode === "grid";
      const isListView = viewMode === "details" || viewMode === "list";
    return (
        <div className="mb-5 flex flex-wrap items-center gap-2.5" data-testid="gallery-filter-toolbar">
            {/* Search */}
            <div className="relative w-full sm:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search assets in gallery..."
                    className="h-10 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm shadow-sm focus:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                    data-testid="gallery-search"
                />
            </div>

            {/* Type filter */}
            <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-10 w-[150px] rounded-lg border-border bg-card shadow-sm" data-testid="gallery-type-trigger">
                    <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                    {KIND_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`gallery-type-opt-${o.value}`}>{o.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Workspace filter */}
            <Select value={workspace} onValueChange={setWorkspace}>
                <SelectTrigger className="h-10 w-[180px] rounded-lg border-border bg-card shadow-sm" data-testid="gallery-workspace-trigger">
                    <SelectValue placeholder="All workspaces" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all" data-testid="gallery-ws-opt-all">All workspaces</SelectItem>
                    {/* <SelectItem value="__none__" data-testid="gallery-ws-opt-none">No workspace</SelectItem> */}
                    {(workspaces || []).map((w) => (
                        <SelectItem key={w.id} value={w.id} data-testid={`gallery-ws-opt-${w.id}`}>{w.name}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Last modified filter */}
            <Select value={modified} onValueChange={setModified}>
                <SelectTrigger className="h-10 w-[170px] rounded-lg border-border bg-card shadow-sm" data-testid="gallery-modified-trigger">
                    <Calendar className="mr-1 h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder="Last modified" />
                </SelectTrigger>
                <SelectContent>
                    {MODIFIED_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`gallery-mod-opt-${o.value}`}>{o.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Clear */}
            <Button
                type="button"
                variant="outline"
                className="h-10 rounded-lg border-border bg-card shadow-sm"
                disabled={!hasFilter}
                onClick={onClear}
                data-testid="gallery-clear"
            >
                Clear
            </Button>

            {/* View toggle on the right */}
            <div className="ml-auto inline-flex rounded-lg border border-border bg-card p-1 shadow-sm" data-testid="gallery-view-toggle-wrap">
                <button
                    type="button"
                    onClick={() => setViewMode("tiles")}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        //viewMode === "tiles" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                         isGridView ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="gallery-view-grid"
                    aria-pressed={isGridView}
                >
                    <LayoutGrid className="h-3.5 w-3.5" /> Grid
                </button>
                <button
                    type="button"
                    onClick={() => setViewMode("details")}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        isListView ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="gallery-view-list"
                    aria-pressed={isListView}
                >
                    <ListIcon className="h-3.5 w-3.5" /> List
                </button>
            </div>
        </div>
    );
}
